import imageCompression from 'browser-image-compression';

/**
 * Highly optimized image compression utility
 * Compresses images before upload to reduce file size and improve performance
 * 
 * @param {File} imageFile - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (imageFile, options = {}) => {
    // Default compression options - highly optimized
    const defaultOptions = {
        maxSizeMB: 0.5, // Maximum size in MB (500KB)
        maxWidthOrHeight: 1920, // Maximum width or height in pixels
        useWebWorker: true, // Use web worker for better performance
        fileType: 'image/jpeg', // Convert to JPEG for better compression
        initialQuality: 0.8, // Initial quality (0-1)
        alwaysKeepResolution: false, // Allow resizing for better compression
        ...options
    };

    try {
        // Check if file is already small enough
        const fileSizeMB = imageFile.size / (1024 * 1024);
        
        // If file is already smaller than max size, return as-is
        if (fileSizeMB <= defaultOptions.maxSizeMB) {
            console.log(`Image already optimized: ${fileSizeMB.toFixed(2)}MB`);
            return imageFile;
        }

        console.log(`Compressing image: ${fileSizeMB.toFixed(2)}MB -> target: ${defaultOptions.maxSizeMB}MB`);
        
        // Compress the image
        const compressedFile = await imageCompression(imageFile, defaultOptions);
        
        const compressedSizeMB = compressedFile.size / (1024 * 1024);
        const compressionRatio = ((1 - compressedFile.size / imageFile.size) * 100).toFixed(1);
        
        console.log(`Compression complete: ${compressedSizeMB.toFixed(2)}MB (${compressionRatio}% reduction)`);
        
        return compressedFile;
    } catch (error) {
        console.error('Error compressing image:', error);
        // Return original file if compression fails
        return imageFile;
    }
};

/**
 * Compress multiple images
 * 
 * @param {FileList|Array<File>} imageFiles - Array of image files to compress
 * @param {Object} options - Compression options
 * @returns {Promise<Array<File>>} - Array of compressed image files
 */
export const compressMultipleImages = async (imageFiles, options = {}) => {
    const filesArray = Array.from(imageFiles);
    const compressedFiles = [];
    
    for (let i = 0; i < filesArray.length; i++) {
        console.log(`Compressing image ${i + 1}/${filesArray.length}`);
        const compressed = await compressImage(filesArray[i], options);
        compressedFiles.push(compressed);
    }
    
    return compressedFiles;
};

